import './config/theme/MuiClassNameOverrides';
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import thunk, { ThunkAction, ThunkDispatch } from 'redux-thunk';
import './index.css';
import App from './App';
import { createStore, applyMiddleware, compose, AnyAction } from 'redux';
import rootReducer from './application/reducers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import GlobalErrorBoundary from '@/components/ErrorBoundary';
import { I18nextProvider } from 'react-i18next';
import i18nMain from './config/i18n';
import { Helmet } from 'react-helmet';
import LtAuth0Provider from './infrastructure/hoc/LtAuth0Provider';
import { InternalDesignProvider } from './config/theme/useMuiTheme';
import LtThemeProvider from './config/theme/LtThemeProvider';
import * as Sentry from '@sentry/react';
import { ContextProvider } from './utils/ContextProvider';
import { BrowserRouter } from 'react-router-dom';

declare global {
  interface Window {
    FreshworksWidget?: (
      arg?: string,
      arg2?: string,
      arg3?: Record<string, string | undefined>,
    ) => void;
    UC_UI: {
      showSecondLayer: () => void;
      updateLanguage(lang: string): void;
      isInitialized(): boolean;
    };
    usetifulTags: Record<string, string | undefined>;
  }
}

export const fontFamily: React.CSSProperties['fontFamily'] = 'Poppins, sans-serif';

const envName = import.meta.env.REACT_APP_PRODUCTION === 'true' ? 'production' : 'local';
Sentry.init({
  dsn: import.meta.env.SENTRY_DSN,
  environment: envName,
  enabled: envName === 'production',
  integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['https://api.lemontaps.com'],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

const composeEnhancers =
  (window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__?.({
    trace: true,
    traceLimit: 25,
  }) as typeof compose) || compose;

const middleware = [thunk];

const store = createStore(rootReducer, composeEnhancers(applyMiddleware(...middleware)));
export type AppStore = typeof store;
export type AppDispatch = ThunkDispatch<RootState, unknown, AnyAction>;
export type RootState = ReturnType<typeof store.getState>;

export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, AnyAction>;

const fontStyles = `
  body ,
  textarea,
  textarea::placeholder,
  input,
  input::placeholder,
  .LtTooltip-tooltip,
  label {
    font-family: ${fontFamily};
  }
`;

/**
 * @deprecated
 * @see https://react.dev/reference/react-dom/render
 * after migration to React 18
 * we use 17 version
 */
ReactDOM.render(
  <>
    <Helmet>
      <style>{fontStyles}</style>
    </Helmet>
    <Provider store={store}>
      <LtAuth0Provider>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <I18nextProvider i18n={i18nMain}>
            <InternalDesignProvider>
              <LtThemeProvider>
                <GlobalErrorBoundary>
                  <BrowserRouter>
                    <ContextProvider>
                      <App />
                    </ContextProvider>
                  </BrowserRouter>
                </GlobalErrorBoundary>
              </LtThemeProvider>
            </InternalDesignProvider>
          </I18nextProvider>
        </LocalizationProvider>
      </LtAuth0Provider>
    </Provider>
  </>,
  document.getElementById('root'),
);
